import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Calendar,
  User as UserIcon,
  AlertTriangle,
  Plus,
  Search,
  Building2,
  Briefcase,
  CheckCircle,
  Clock,
  XCircle,
  Play,
  ChevronRight
} from 'lucide-react';
import { inspectionsService, facilitiesService, usersService, clientsService } from '../services';
import { Inspection, Facility, User, Client } from '../types';
import { formatShortDate } from '../utils/dateUtils';
import './Inspections.css';

interface InspectionTaskStats {
  total: number;
  complete: number;
  pending: number;
}

const Inspections: React.FC = () => {
  usePageTitle('Inspections');
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [taskStats, setTaskStats] = useState<Record<string, InspectionTaskStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [facilityFilter, setFacilityFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [showNewInspectionModal, setShowNewInspectionModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [newInspectionFacilityId, setNewInspectionFacilityId] = useState('');
  const [newInspectionCreatedById, setNewInspectionCreatedById] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (showNewInspectionModal) {
      usersService.getUsers().then(setUsers).catch(() => setUsers([]));
    } else {
      setNewInspectionFacilityId('');
      setNewInspectionCreatedById('');
    }
  }, [showNewInspectionModal]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      void usersService.getUsers().then(setUsers).catch(() => {});
      const [inspectionsData, facilitiesData, clientsData] = await Promise.all([
        inspectionsService.getInspections(),
        facilitiesService.getFacilities(),
        clientsService.getClients(),
      ]);

      setInspections(inspectionsData);
      setFacilities(facilitiesData);
      setClients(clientsData);

      const statsMap: Record<string, InspectionTaskStats> = {};
      inspectionsData.forEach(inspection => {
        const total = inspection.task_count || 0;
        const complete = inspection.complete_task_count || 0;
        statsMap[inspection.id] = { total, complete, pending: total - complete };
      });
      setTaskStats(statsMap);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load inspections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInspection = async (facilityId: string, createdById: string) => {
    try {
      const newInspection = await inspectionsService.createInspection({
        facility_id: facilityId,
        created_by_id: createdById,
        type: 'routine'
      });
      setInspections([...inspections, newInspection]);
      setShowNewInspectionModal(false);
    } catch (err) {
      console.error('Error creating inspection:', err);
      setError('Failed to create inspection. Please try again.');
    }
  };

  const handleSubmitNewInspection = () => {
    if (newInspectionFacilityId && newInspectionCreatedById) {
      handleCreateInspection(newInspectionFacilityId, newInspectionCreatedById);
    }
  };

  const handleUpdateInspectionStatus = async (inspectionId: string, status: string) => {
    try {
      const updatedInspection = await inspectionsService.updateInspection(inspectionId, {
        status: status as any
      });

      setInspections(inspections.map(inspection =>
        inspection.id === inspectionId ? updatedInspection : inspection
      ));
    } catch (err) {
      console.error('Error updating inspection:', err);
      setError('Failed to update inspection status. Please try again.');
    }
  };

  const facilityMap = useMemo(
    () => Object.fromEntries(facilities.map(f => [f.id, f])),
    [facilities]
  );

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map(c => [c.id, c])),
    [clients]
  );

  const getFacilityName = (facilityId: string) =>
    facilityMap[facilityId]?.name ?? 'Unknown Facility';

  const getClientForFacility = (facilityId: string): Client | undefined => {
    const facility = facilityMap[facilityId];
    if (!facility) return undefined;
    return clientMap[facility.client_id];
  };

  const getClientName = (facilityId: string) =>
    getClientForFacility(facilityId)?.name ?? 'Unknown Client';

  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach(u => map.set(u.id, u.name));
    return map;
  }, [users]);

  const getUserName = (userId: string) => {
    return userMap.get(userId) || 'Unknown User';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'not_started':
        return <Clock size={16} />;
      case 'in_progress':
        return <Play size={16} />;
      case 'complete':
        return <CheckCircle size={16} />;
      case 'cancelled':
        return <XCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const facilityName = getFacilityName(inspection.facility_id);
    const clientName = getClientName(inspection.facility_id);
    const matchesSearch =
      facilityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inspection.status === statusFilter;
    const matchesFacility = facilityFilter === 'all' || inspection.facility_id === facilityFilter;
    const matchesClient = clientFilter === 'all' || getClientForFacility(inspection.facility_id)?.id === clientFilter;

    return matchesSearch && matchesStatus && matchesFacility && matchesClient;
  });

  const selectedFacility = facilities.find(f => f.id === newInspectionFacilityId);
  const facilityUsers = selectedFacility
    ? users.filter(u => !u.client_id || u.client_id === selectedFacility.client_id)
    : [];

  if (loading) {
    return (
      <div className="inspections">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="inspections">
      <div className="inspections-header">
        <div className="header-content">
          <h1>Inspections</h1>
          <p>Schedule and track facility inspections</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowNewInspectionModal(true)}
        >
          <Plus size={20} />
          Schedule Inspection
        </button>
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      <div className="inspections-controls">
        <div className="search-filters">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search inspections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="complete">Complete</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={facilityFilter}
            onChange={(e) => setFacilityFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Facilities</option>
            {facilities.map(facility => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>

          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="inspections-content">
        {filteredInspections.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} />
            <h3>No Inspections Found</h3>
            <p>
              {inspections.length === 0
                ? 'No inspections have been created yet.'
                : 'No inspections match your current filters.'}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowNewInspectionModal(true)}
            >
              <Plus size={20} />
              Schedule Inspection
            </button>
          </div>
        ) : (
          filteredInspections.map(inspection => (
            <div
              key={inspection.id}
              className="inspection-card card clickable"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/inspections/${inspection.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/inspections/${inspection.id}`);
                }
              }}
            >
              <div className="inspection-header">
                <div className="inspection-icon">
                  <ClipboardList size={24} />
                </div>
                <div className={`inspection-status ${inspection.status.replace('_', '-')}`}>
                  {getStatusIcon(inspection.status)}
                  {inspection.status.replace('_', ' ')}
                </div>
              </div>

              <div className="inspection-content">
                <div className="inspection-title">
                  {getFacilityName(inspection.facility_id)} Inspection
                </div>

                <div className="inspection-details">
                  <div className="detail-item">
                    <Building2 size={16} />
                    <span>{getFacilityName(inspection.facility_id)}</span>
                  </div>
                  <div className="detail-item">
                    <Briefcase size={16} />
                    <span>{getClientName(inspection.facility_id)}</span>
                  </div>
                  <div className="detail-item">
                    <Calendar size={16} />
                    <span>Created: {formatShortDate(inspection.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <UserIcon size={16} />
                    <span>Created by: {getUserName(inspection.created_by_id)}</span>
                  </div>
                </div>

                <div className="inspection-stats">
                  <div className="stat">
                    <div className="stat-value">{taskStats[inspection.id]?.total ?? 0}</div>
                    <div className="stat-label">Tasks</div>
                  </div>
                  <div className="stat">
                    <div className="stat-value">{taskStats[inspection.id]?.complete ?? 0}</div>
                    <div className="stat-label">Complete</div>
                  </div>
                  <div className="stat">
                    <div className="stat-value">{taskStats[inspection.id]?.pending ?? 0}</div>
                    <div className="stat-label">Pending</div>
                  </div>
                </div>
              </div>

              <div className="inspection-actions">
                <button
                  className="btn btn-primary"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (inspection.status === 'not_started') {
                      await handleUpdateInspectionStatus(inspection.id, 'in_progress');
                      navigate(`/inspections/${inspection.id}`);
                    } else {
                      const nextStatus = inspection.status === 'in_progress'
                        ? 'complete'
                        : 'not_started';
                      handleUpdateInspectionStatus(inspection.id, nextStatus);
                    }
                  }}
                >
                  {inspection.status === 'not_started' && 'Start Inspection'}
                  {inspection.status === 'in_progress' && 'Complete Inspection'}
                  {inspection.status === 'complete' && 'Restart Inspection'}
                  {inspection.status === 'cancelled' && 'Restart Inspection'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/inspections/${inspection.id}`);
                  }}
                  aria-label="View inspection details"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          ))
        )}

      </div>

      {showNewInspectionModal && (
        <div className="modal-overlay" onClick={() => setShowNewInspectionModal(false)}>
          <div className="modal card" onClick={(e) => e.stopPropagation()}>
            <h3>Schedule Inspection</h3>
            <p className="modal-hint">Select facility and creator to schedule an inspection.</p>
            <div className="form-group">
              <label htmlFor="new-inspection-facility">Facility</label>
              <select
                id="new-inspection-facility"
                value={newInspectionFacilityId}
                onChange={(e) => {
                  setNewInspectionFacilityId(e.target.value);
                  setNewInspectionCreatedById('');
                }}
                className="form-select"
              >
                <option value="">Select facility</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="new-inspection-created-by">Created by</label>
              <select
                id="new-inspection-created-by"
                value={newInspectionCreatedById}
                onChange={(e) => setNewInspectionCreatedById(e.target.value)}
                className="form-select"
                disabled={!newInspectionFacilityId}
              >
                <option value="">
                  {newInspectionFacilityId ? 'Select staff member' : 'Select a facility first'}
                </option>
                {facilityUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowNewInspectionModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmitNewInspection}
                disabled={!newInspectionFacilityId || !newInspectionCreatedById}
              >
                Schedule Inspection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inspections;
